import { Vector2 } from "../../common/vector2.js";
import { ApiError } from "../../api/http_client.js";
import type { ReviveOfferDto } from "../../api/dto/player.dto.js";
import { purchaseItem } from "../../offline/economy_gateway.js";
import { adoptEconomyFromPlayer } from "../../api/player_sync.js";
import { logVirtualCurrencySpent } from "../../analytics/appsflyer_events.js";
import { ImgID } from "../../image_loader/img_id.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { OverlayFactory } from "../overlay/overlay_factory.js";
import { OverlayId } from "../overlay/overlay_ids.js";
import { setButtonTextOutline } from "../common_ui_elements/button_text_outline.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIAdaptiveElementsByTextHorisontal } from "../common_ui_elements/ui_adaptive_elements_by_text_horisontal.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, UITextOptions } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { addMirroredDialogBg } from "../common_ui_elements/mirrored_dialog_bg.js";
import { DialogCoinsBadge } from "../common_ui_elements/dialog_coins_badge.js";

interface IOfferReward
{
  img_id: ImgID;
  x:      number;
  y:      number;
  w:      number;
  h:      number;
  text:   string;
}

export interface TowerFailDialogParams
{
  /** Wins already collected on the floor the player is climbing. */
  wins_progress: number;
  /** Wins that floor needs, i.e. the denominator of the badge counter. */
  wins_required: number;
  /** The revive offer the continue overlay just showed; drives the buy button. */
  offer:          ReviveOfferDto;
  purchase_index: number;
  on_close?: () => void;
  on_buy?:   ( shots: number ) => void;
}

export class TowerFailDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.TOWER_FAIL_DIALOG;

  private static readonly DESIGN_W = 1018;
  private static readonly DESIGN_H = 1879;

  private static readonly CONTENT_W = 909;
  private static readonly CONTENT_H = 1028;

  private static readonly OFFER_W = 1018;
  private static readonly OFFER_H = 442;
  private static readonly OFFER_BOTTOM_MARGIN = 120;

  private static readonly FRAME_W = 777;
  private static readonly FRAME_H = 395;
  private static readonly FRAME_Y = 250;

  private static readonly TEXT_FONT_SIZE = 50;
  private static readonly TEXT_OUTLINE_W = 14;
  private static readonly TEXT_OUTLINE_COLOR = "#103A92";

  private static readonly REWARD_FONT_SIZE = 38;

  // TODO: replace with the real store price once the offer is wired up.
  private static readonly OFFER_PRICE = "123,99 ГРН";

  private static readonly OFFER_MOVES = 15;

  private static readonly OFFER_REWARDS: ReadonlyArray<IOfferReward> = [
    { img_id: ImgID.UI_DailyTasksTaskCannon,    x: 32,  y: 19, w: 122, h: 122, text: "+15" },
    { img_id: ImgID.UI_GameStartBigBallBooster, x: 190, y: 21, w: 120, h: 120, text: "x3" },
    { img_id: ImgID.UI_ShopBlockImg,            x: 356, y: 23, w: 120, h: 120, text: "1000" },
  ];

  private content!:   UIElementHolder;
  private offer!:     UINineSliceElement;
  private close_btn!: UIImage;

  private progress_text: UITextElement | null = null;
  private coins_badge:   DialogCoinsBadge | null = null;

  private content_x: number = 0;
  private content_y: number = 0;

  private body_x: number = 0;
  private body_y: number = 0;

  private params: TowerFailDialogParams;
  private action_btn: UINineSliceElement | null = null;
  private action_btn_texts: UITextElement[] = [];

  private purchase_inflight = false;
  private is_bought        = false;
  private purchased_shots  = 0;

  constructor( params: TowerFailDialogParams )
  {
    const body_w = LayoutScaler.widthFromDesign( TowerFailDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( TowerFailDialog.DESIGN_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, false );

    this.params = params;
    this.body_x = body_x;
    this.body_y = body_y;

    this.createContent();
    // TODO: temporarily disabled. When bringing the offer block back, also
    // restore the content block position in createContent().
    // this.createOffer();
    this.createTitle();
    this.createFrame();
    this.createActionButton();
    this.createCloseButton();
    this.createCoinBadge();
  }

  private textOptions(): UITextOptions
  {
    return {
      font_size:      LayoutScaler.textSizeFromDesign( TowerFailDialog.TEXT_FONT_SIZE ),
      font_weight:    900,
      text_color:     ColorScheme.white,
      text_style:     TextStyleScheme.geologica,
      text_align:     MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width:  LayoutScaler.textBorderSizeFromDesign( TowerFailDialog.TEXT_OUTLINE_W ),
      outline_color:  TowerFailDialog.TEXT_OUTLINE_COLOR,
      auto_wrap:      false,
      scale_to_width: true,
    };
  }

  private createContent(): void
  {
    const content_w = LayoutScaler.widthFromDesign( TowerFailDialog.CONTENT_W );
    const content_h = LayoutScaler.heightFromDesign( TowerFailDialog.CONTENT_H );

    // TODO: the content is screen-centered only while createOffer() is
    // commented out and it is the single block on the dialog. Once the offer
    // block returns, move the content back up so the two do not overlap.
    this.content_x = ( this.rect.getW() - content_w ) / 2;
    this.content_y = ( DialogManager.getHeight() - content_h ) / 2 - this.body_y;

    const content = new UIElementHolder( this.ctx, this.content_x, this.content_y, content_w, content_h );
    this.addChild( content );
    this.content = content;

    addMirroredDialogBg( this.ctx, content, 0, 0, TowerFailDialog.CONTENT_W, TowerFailDialog.CONTENT_H );
  }

  private createOffer(): void
  {
    const offer_w = LayoutScaler.widthFromDesign( TowerFailDialog.OFFER_W );
    const offer_h = LayoutScaler.heightFromDesign( TowerFailDialog.OFFER_H );

    const bottom_margin = LayoutScaler.heightFromDesign( TowerFailDialog.OFFER_BOTTOM_MARGIN );
    const offer_y = DialogManager.getHeight() - this.body_y - offer_h - bottom_margin;

    const offer = new UINineSliceElement(
      this.ctx,
      ( this.rect.getW() - offer_w ) / 2,
      offer_y,
      offer_w,
      offer_h,
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( offer );
    this.offer = offer;

    this.createOfferSubstrate( offer, offer_w );
    this.createOfferPanel( offer, offer_w );
    this.createOfferText( offer, offer_w );
  }

  private createOfferSubstrate( offer: UINineSliceElement, offer_w: number ): void
  {
    const design_substrate_w = 668;
    const design_substrate_h = 162;
    const design_substrate_y = -15;
    const design_text_y      = 18;
    const design_text_h      = 69;

    const MAGIC_NUM_CORRECTION = 0.8;

    const substrate_w = LayoutScaler.widthFromDesign( design_substrate_w );
    const substrate_h = LayoutScaler.heightFromDesign( design_substrate_h );

    const substrate_x = ( offer_w - substrate_w ) / 2;
    const substrate_y = LayoutScaler.yFromDesign( design_substrate_y );
    const substrate = new UIImage( this.ctx, substrate_x, substrate_y, substrate_w, substrate_h, ImgID.UI_ContinueOfferTitleBadge );
    offer.addChild( substrate );

    const text_w = substrate_w * MAGIC_NUM_CORRECTION;

    substrate.addChild( new UITextElement(
      this.ctx,
      ( substrate_w - text_w ) / 2,
      LayoutScaler.yFromDesign( design_text_y ),
      text_w,
      LayoutScaler.heightFromDesign( design_text_h ),
      {
        localization_key: "TOWER_FAIL_DIALOG.OFFER.BADGE",
        ...this.textOptions(),
      }
    ) );
  }

  private createOfferPanel( offer: UINineSliceElement, offer_w: number ): void
  {
    const design_panel_w = 882;
    const design_panel_h = 193;
    const design_panel_y = 203;

    const panel_w = LayoutScaler.widthFromDesign( design_panel_w );

    const panel = new UINineSliceElement(
      this.ctx,
      ( offer_w - panel_w ) / 2,
      LayoutScaler.yFromDesign( design_panel_y ),
      panel_w,
      LayoutScaler.heightFromDesign( design_panel_h ),
      ImgID.UI_TowerFailDarkBlueSubstrate,
      "tower_fail_dark_blue_substrate"
    );
    offer.addChild( panel );

    this.createOfferPanelBtn( panel, panel_w );

    TowerFailDialog.OFFER_REWARDS.forEach( ( reward, i ) =>
    {
      this.createOfferReward( panel, reward );

      const next_reward = TowerFailDialog.OFFER_REWARDS[ i + 1 ];
      if ( next_reward )
        this.createOfferPlus( panel, reward, next_reward );
    } );
  }

  private createOfferPanelBtn( panel: UINineSliceElement, panel_w: number ): void
  {
    // 339x193 shrunk by 6% and re-centered on the same point.
    const design_btn_w        = 319;
    const design_btn_h        = 181;
    const design_btn_y        = 6;
    const design_btn_inset_x  = 10;

    const design_price_font_size = 50;

    const MAGIC_NUM_CORRECTION = 0.8;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );

    const btn = new UIImage(
      this.ctx,
      panel_w - btn_w - LayoutScaler.widthFromDesign( design_btn_inset_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      btn_w,
      btn_h,
      ImgID.UI_TowerFailGreenBtnHalf,
      undefined,
      true
    );
    btn.setOnClick( () => {} );
    panel.addChild( btn );

    const price_w = btn_w * MAGIC_NUM_CORRECTION;

    btn.addChild( new UITextElement(
      this.ctx,
      ( btn_w - price_w ) / 2,
      0,
      price_w,
      btn_h,
      {
        text:           TowerFailDialog.OFFER_PRICE,
        font_size:      LayoutScaler.textSizeFromDesign( design_price_font_size ),
        font_weight:    800,
        text_color:     ColorScheme.white,
        text_style:     TextStyleScheme.geologica,
        text_align:     MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap:      false,
        scale_to_width: true,
        ...TextStyleScheme.buttonShadow(),
      }
    ), true );
  }

  private createOfferReward( panel: UINineSliceElement, reward: IOfferReward ): void
  {
    const design_text_h        = 60;
    const design_text_slop     = 20;
    const design_text_offset_y = 10;

    const icon_w = LayoutScaler.widthFromDesign( reward.w );
    const icon_h = LayoutScaler.heightFromDesign( reward.h );

    const icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( reward.x ),
      LayoutScaler.yFromDesign( reward.y ),
      icon_w,
      icon_h,
      reward.img_id
    );
    panel.addChild( icon );

    const text_slop = LayoutScaler.widthFromDesign( design_text_slop );
    const text_h    = LayoutScaler.heightFromDesign( design_text_h );

    icon.addChild( new UITextElement(
      this.ctx,
      -text_slop,
      icon_h - text_h + LayoutScaler.yFromDesign( design_text_offset_y ),
      icon_w + text_slop * 2,
      text_h,
      {
        text: reward.text,
        ...this.textOptions(),
        font_size: LayoutScaler.textSizeFromDesign( TowerFailDialog.REWARD_FONT_SIZE ),
      }
    ), true );
  }

  private createOfferPlus( panel: UINineSliceElement, left: IOfferReward, right: IOfferReward ): void
  {
    const design_plus_x = left.x + left.w;
    const design_plus_w = right.x - design_plus_x;

    panel.addChild( new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_plus_x ),
      LayoutScaler.yFromDesign( left.y ),
      LayoutScaler.widthFromDesign( design_plus_w ),
      LayoutScaler.heightFromDesign( left.h ),
      {
        text: "+",
        ...this.textOptions(),
        font_size: LayoutScaler.textSizeFromDesign( TowerFailDialog.REWARD_FONT_SIZE ),
      }
    ) );
  }

  private createOfferText( offer: UINineSliceElement, offer_w: number ): void
  {
    const design_text_y = 129;
    const design_text_h = 69;

    const MAGIC_NUM_CORRECTION = 0.8;

    const text_w = offer_w * MAGIC_NUM_CORRECTION;

    offer.addChild( new UITextElement(
      this.ctx,
      ( offer_w - text_w ) / 2,
      LayoutScaler.yFromDesign( design_text_y ),
      text_w,
      LayoutScaler.heightFromDesign( design_text_h ),
      {
        localization_key:     "TOWER_FAIL_DIALOG.OFFER.DESCRIPTION",
        text_variable_params: [ String( TowerFailDialog.OFFER_MOVES ) ],
        ...this.textOptions(),
      }
    ) );
  }

  private createTitle(): void
  {
    const design_title_y         = 90;
    const design_title_h         = 136;
    const design_font_size       = 100;
    const design_outline_w       = 18;
    const design_outline_offset_y = 3;
    const design_shadow_blur     = 10;
    const design_shadow_offset_y = 5;

    const MAGIC_NUM_CORRECTION = 0.86;

    const content_w = LayoutScaler.widthFromDesign( TowerFailDialog.CONTENT_W );
    const title_w   = content_w * MAGIC_NUM_CORRECTION;

    const title = new UITextElement(
      this.ctx,
      ( content_w - title_w ) / 2,
      LayoutScaler.yFromDesign( design_title_y ),
      title_w,
      LayoutScaler.heightFromDesign( design_title_h ),
      {
        localization_key: "CONTINUE_DIALOG.TITLE",
        font_size:      LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight:    900,
        text_color:     ColorScheme.white,
        text_style:     TextStyleScheme.geologica,
        text_align:     MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width:  LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color:  ColorScheme.booster_dialog_title_outline,
        outline_offset_y: LayoutScaler.yFromDesign( design_outline_offset_y ),
        shadow_blur:    LayoutScaler.widthFromDesign( design_shadow_blur ),
        shadow_color:   ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( design_shadow_offset_y ),
        shadow_offset_x: 0,
        auto_wrap:      false,
        scale_to_width: true,
      }
    );
    this.content.addChild( title );
  }

  private createFrame(): void
  {
    const content_w = LayoutScaler.widthFromDesign( TowerFailDialog.CONTENT_W );
    const frame_w   = LayoutScaler.widthFromDesign( TowerFailDialog.FRAME_W );

    const frame = new UIImage(
      this.ctx,
      ( content_w - frame_w ) / 2,
      LayoutScaler.yFromDesign( TowerFailDialog.FRAME_Y ),
      frame_w,
      LayoutScaler.heightFromDesign( TowerFailDialog.FRAME_H ),
      ImgID.UI_PaternedBg
    );
    this.content.addChild( frame );

    this.createProgressCounter( frame );
    this.createFrameCaption( frame );
  }

  private createProgressCounter( frame: UIImage ): void
  {
    const design_icon_w          = 188;
    const design_icon_h          = design_icon_w * 240 / 190;
    const design_icon_y          = 267 - design_icon_h;
    const design_text_w          = 120;
    const design_text_h          = 44;
    const design_text_y          = design_icon_h * 201 / 240 - design_text_h / 2;
    const design_font_size       = 30;
    const design_shadow_blur     = 3;
    const design_shadow_offset_y = 3;

    const frame_w = LayoutScaler.widthFromDesign( TowerFailDialog.FRAME_W );
    const icon_w  = LayoutScaler.widthFromDesign( design_icon_w );
    const icon_h  = LayoutScaler.heightFromDesign( design_icon_h );

    const icon = new UIImage(
      this.ctx,
      ( frame_w - icon_w ) / 2,
      LayoutScaler.yFromDesign( design_icon_y ),
      icon_w,
      icon_h,
      ImgID.UI_AdventureWidget
    );
    frame.addChild( icon );

    const text_w = LayoutScaler.widthFromDesign( design_text_w );
    const text_h = LayoutScaler.heightFromDesign( design_text_h );

    const progress_text = new UITextElement(
      this.ctx,
      ( icon_w - text_w ) / 2,
      LayoutScaler.yFromDesign( design_text_y ),
      text_w,
      text_h,
      {
        text: `${ this.params.wins_progress }/${ this.params.wins_required }`,
        font_size:       LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight:     900,
        text_style:      TextStyleScheme.geologica,
        text_color:      ColorScheme.white,
        shadow_blur:     LayoutScaler.widthFromDesign( design_shadow_blur ),
        shadow_color:    ColorScheme.black_transparent_80,
        shadow_offset_y: LayoutScaler.yFromDesign( design_shadow_offset_y ),
        auto_wrap:       false,
        scale_to_width:  true,
      }
    );
    icon.addChild( progress_text, true );
    this.progress_text = progress_text;
  }

  private createFrameCaption( frame: UIImage ): void
  {
    const design_caption_y         = 270;
    const design_caption_h         = 90;
    const design_caption_font_size = 54;

    const MAGIC_NUM_CORRECTION = 0.86;

    const frame_w   = LayoutScaler.widthFromDesign( TowerFailDialog.FRAME_W );
    const caption_w = frame_w * MAGIC_NUM_CORRECTION;

    const caption = new UITextElement(
      this.ctx,
      ( frame_w - caption_w ) / 2,
      LayoutScaler.yFromDesign( design_caption_y ),
      caption_w,
      LayoutScaler.heightFromDesign( design_caption_h ),
      {
        localization_key: "TOWER_FAIL_DIALOG.OFFER.TEXT",
        color_map:      { highlight: ColorScheme.continue_overlay_caption_highlight },
        font_size:      LayoutScaler.textSizeFromDesign( design_caption_font_size ),
        font_weight:    900,
        text_color:     ColorScheme.menu_top_el_text,
        text_style:     TextStyleScheme.geologica,
        text_align:     MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color:  ColorScheme.white,
        outline_width:  LayoutScaler.textBorderSizeFromDesign( 8 ),
        auto_wrap:      false,
        scale_to_width: true,
      }
    );
    frame.addChild( caption );
  }

  private createActionButton(): void
  {
    const design_btn_w       = 660;
    const design_btn_h       = 265;
    const design_btn_y       = 670;
    const design_font_size   = 64;
    const design_outline_w   = 8;
    const design_outline_offset_y = 2;
    const design_icon_size   = 104;
    const design_content_gap = 0;

    const design_btn_x = ( TowerFailDialog.CONTENT_W - design_btn_w ) / 2;

    const price = this.params.offer.price;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );

    const btn = this.action_btn = new UINineSliceElement(
      this.ctx,
      this.content_x + LayoutScaler.xFromDesign( design_btn_x ),
      this.content_y + LayoutScaler.yFromDesign( design_btn_y ),
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const text_options: UITextOptions = {
      font_size:      LayoutScaler.textSizeFromDesign( design_font_size ),
      font_weight:    900,
      text_color:     ColorScheme.white,
      text_style:     TextStyleScheme.geologica,
      text_align:     MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap:      false,
      outline_color:  ColorScheme.more_lives_buy_outline,
      outline_width:  LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
      outline_offset_y: LayoutScaler.yFromDesign( design_outline_offset_y ),
      ...TextStyleScheme.buttonShadow(),
    };

    const buy_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "BUY",
        ...text_options,
      }
    );

    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const icon = new UIImage(
      this.ctx, 0, 0, icon_size, icon_size, ImgID.IconCoin
    );

    const price_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        text: price.toString(),
        ...text_options,
      }
    );

    const content = new UIAdaptiveElementsByTextHorisontal(
      this.ctx, 0, 0, btn_w, btn_h,
      [ buy_text, icon, price_text ],
      LayoutScaler.widthFromDesign( design_content_gap )
    );
    btn.addChild( content, true );
    this.action_btn_texts = [ buy_text, price_text ];

    btn.setOnClick( () =>
    {
      if ( StatisticsStorage.load().coins < price )
      {
        // Keep the game and this dialog alive under the coin shop, the way
        // BuyBoosterDialog does — the next tap reads the fresh balance.
        OverlayFactory.openOverlay( OverlayId.SHOP_OVERLAY, { onShopClosed: () => {} } );
        return;
      }

      this.buyContinue( btn, price );
    } );

    this.addChild( btn );
  }

  /**
   * Buys the same revive offer the continue overlay showed. Winning the level
   * after this means spendHeartOnLevelFail() never runs, so the streak lives.
   */
  private buyContinue( btn: UINineSliceElement, price: number ): void
  {
    if ( this.purchase_inflight )
      return;

    this.purchase_inflight = true;
    this.setActionButtonActive( false );

    purchaseItem( "continue_balls", {
      purchase_index: this.params.purchase_index,
      price,
      qty: this.params.offer.shots,
    } )
      .then( r =>
      {
        adoptEconomyFromPlayer( r.player );
        void logVirtualCurrencySpent( {
          currency_name: 'coins',
          amount: r.purchased.price,
          sink: 'continue_balls',
        } );
        this.is_bought = true;
        this.purchased_shots = r.purchased.qty;
        this.close();
      } )
      .catch( e =>
      {
        console.warn( '[Economy] tower fail continue purchase failed', e );
        this.purchase_inflight = false;
        const sold_out = e instanceof ApiError && e.status === 409;
        btn.setClickable( !sold_out );
        this.setActionButtonActive( !sold_out );
      } );
  }

  private setActionButtonActive( active: boolean ): void
  {
    this.action_btn?.setImgId( active ? ImgID.UI_GreenFrameBtn : ImgID.UI_NonActiveBtn, active ? "frame_btn" : "non_active_btn" );
    setButtonTextOutline( this.action_btn_texts, active, ColorScheme.more_lives_buy_outline );
    this.invalidateSubtree();
  }

  private createCloseButton(): void
  {
    const design_btn_size = 106;
    const design_btn_x    = 795;
    const design_btn_y    = -35;

    const btn = new UIImage(
      this.ctx,
      this.content_x + LayoutScaler.xFromDesign( design_btn_x ),
      this.content_y + LayoutScaler.yFromDesign( design_btn_y ),
      LayoutScaler.widthFromDesign( design_btn_size ),
      LayoutScaler.heightFromDesign( design_btn_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => this.close() );
    this.addChild( btn );
    this.close_btn = btn;
  }

  private createCoinBadge(): void
  {
    this.coins_badge = new DialogCoinsBadge( this.ctx, this );
    this.addChild( this.coins_badge );
  }

  protected override playAppearAnimation(
    from_scale: number        = 0.7,
    overshoot_scale: number   = 1.1,
    final_scale: number       = 1.0,
    scale_up_duration: number = 136,
    settle_duration: number   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public override update( now: number ): void
  {
    super.update( now );
    this.coins_badge?.sync( now );
  }

  public override onDialogWillClose(): void
  {
    if ( this.is_bought )
    {
      this.params.on_buy?.( this.purchased_shots );
      return;
    }

    this.params.on_close?.();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public static open( params: TowerFailDialogParams ): TowerFailDialog
  {
    return DialogManager.openPooled( "TowerFailDialog", params, () => new TowerFailDialog( params ) ) as TowerFailDialog;
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse( params: TowerFailDialogParams ): void
  {
    this.params = params;
    this.purchase_inflight = false;
    this.is_bought = false;
    this.purchased_shots = 0;
    this.progress_text?.setText( `${ params.wins_progress }/${ params.wins_required }` );
    this.action_btn?.setClickable( true );
    this.setActionButtonActive( true );
  }

  public name(): string
  {
    return "TowerFailDialog";
  }
}
